import React, { useState } from 'react';
import { Decoupage, Tranche, TypeTransaction } from '../types';
import './GestionDecoupages.css';

const HEURES = Array.from({ length: 24 }, (_, i) => i);
const TYPE_LABELS: Record<TypeTransaction, string> = { depot: 'Dépôt', retrait: 'Retrait', tous: 'Tous' };

function fmtH(h: number): string {
  return `${String(h).padStart(2, '0')}h`;
}

function nouvelleTranche(id: number, pourcentage = 100): Tranche {
  return { id, heureDebut: 0, heureFin: 23, typeTransaction: 'depot', pourcentage };
}

interface FormState {
  nom: string;
  tranches: Tranche[];
}

const FORM_INIT: FormState = {
  nom: '',
  tranches: [nouvelleTranche(1, 100)],
};

interface Props {
  decoupages: Decoupage[];
  onAjouter: (d: Decoupage) => void;
  onSupprimer: (id: number) => void;
  onModifier: (d: Decoupage) => void;
}

export default function GestionDecoupages({ decoupages, onAjouter, onSupprimer, onModifier }: Props) {
  const [formVisible, setFormVisible] = useState(false);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(FORM_INIT);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function ouvrirFormulaire(): void {
    setForm(FORM_INIT);
    setEnEdition(null);
    setErrors({});
    setFormVisible(true);
  }

  function editerDecoupage(d: Decoupage): void {
    setForm({ nom: d.nom, tranches: d.tranches.map(t => ({ ...t })) });
    setEnEdition(d.id);
    setErrors({});
    setFormVisible(true);
  }

  function fermerFormulaire(): void {
    setFormVisible(false);
    setEnEdition(null);
    setForm(FORM_INIT);
    setErrors({});
  }

  function ajouterTranche(): void {
    const total = form.tranches.reduce((s, t) => s + (Number(t.pourcentage) || 0), 0);
    const reste = Math.max(0, 100 - total);
    setForm(f => ({
      ...f,
      tranches: [...f.tranches, nouvelleTranche(Date.now(), reste)],
    }));
  }

  function supprimerTranche(id: number): void {
    setForm(f => ({ ...f, tranches: f.tranches.filter(t => t.id !== id) }));
  }

  function updateTranche(id: number, field: keyof Tranche, value: number | string): void {
    setForm(f => ({
      ...f,
      tranches: f.tranches.map(t => t.id === id ? { ...t, [field]: value } : t),
    }));
  }

  function valider(): void {
    const errs: Record<string, string> = {};
    if (!form.nom.trim()) errs.nom = 'Donnez un nom au découpage';
    if (form.tranches.length === 0) errs.tranches = 'Ajoutez au moins une tranche';

    const totalPct = form.tranches.reduce((s, t) => s + (Number(t.pourcentage) || 0), 0);
    if (Math.abs(totalPct - 100) > 0.01)
      errs.totalPct = `La somme des pourcentages est ${totalPct}% — elle doit être égale à 100%`;

    form.tranches.forEach((t, i) => {
      if (t.heureDebut > t.heureFin)
        errs[`tranche_heure_${i}`] = `Tranche ${i + 1} : l'heure de fin doit être ≥ heure de début`;
      if (!(Number(t.pourcentage) > 0))
        errs[`tranche_pct_${i}`] = `Tranche ${i + 1} : le pourcentage doit être > 0`;
    });

    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const decoupage: Decoupage = {
      id: enEdition ?? Date.now(),
      nom: form.nom.trim(),
      tranches: form.tranches.map(t => ({
        ...t,
        heureDebut: Number(t.heureDebut),
        heureFin: Number(t.heureFin),
        pourcentage: Number(t.pourcentage),
      })),
    };

    enEdition ? onModifier(decoupage) : onAjouter(decoupage);
    fermerFormulaire();
  }

  const totalPct = form.tranches.reduce((s, t) => s + (Number(t.pourcentage) || 0), 0);
  const totalOk = Math.abs(totalPct - 100) < 0.01;

  return (
    <div className="dec-container">
      <div className="dec-page-header">
        <div>
          <h2 className="dec-title">Découpages horaires</h2>
          <p className="dec-sub">
            Définissez comment répartir le montant de chaque bénéficiaire sur des tranches horaires.
            Chaque découpage s'applique ensuite aux résultats d'un calcul.
          </p>
        </div>
        <button className="btn-primary dec-btn-add" onClick={ouvrirFormulaire}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Nouveau découpage
        </button>
      </div>

      {formVisible && (
        <div className="dec-form-card">
          <div className="dec-form-header">
            <h3>{enEdition ? 'Modifier le découpage' : 'Nouveau découpage horaire'}</h3>
            <button className="dec-close-btn" onClick={fermerFormulaire} aria-label="Fermer">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className="dec-form-body">
            <div className="form-group">
              <label className="form-label">Nom du découpage</label>
              <input
                type="text"
                placeholder="ex : Horaires de la journée"
                value={form.nom}
                onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
              />
              {errors.nom && <p className="form-error">{errors.nom}</p>}
            </div>

            <div className="form-group">
              <div className="tranche-label-row">
                <label className="form-label">Tranches horaires</label>
                <span className={`total-pct-badge ${totalOk ? 'ok' : 'err'}`}>
                  Total : {totalPct}%{totalOk ? ' ✓' : ' ✗ (doit être 100%)'}
                </span>
              </div>

              {errors.tranches && <p className="form-error">{errors.tranches}</p>}
              {errors.totalPct && <p className="form-error">{errors.totalPct}</p>}

              <div className="tranches-wrap">
                <div className="tranche-header">
                  <span>De</span>
                  <span>À</span>
                  <span>Type</span>
                  <span>%</span>
                  <span></span>
                </div>

                {form.tranches.map((t, i) => (
                  <div key={t.id} className="tranche-row">
                    <select
                      value={t.heureDebut}
                      onChange={e => updateTranche(t.id, 'heureDebut', Number(e.target.value))}
                    >
                      {HEURES.map(h => <option key={h} value={h}>{fmtH(h)}</option>)}
                    </select>
                    <select
                      value={t.heureFin}
                      onChange={e => updateTranche(t.id, 'heureFin', Number(e.target.value))}
                    >
                      {HEURES.map(h => <option key={h} value={h}>{fmtH(h)}</option>)}
                    </select>
                    <select
                      value={t.typeTransaction}
                      onChange={e => updateTranche(t.id, 'typeTransaction', e.target.value as TypeTransaction)}
                    >
                      <option value="depot">Dépôt</option>
                      <option value="retrait">Retrait</option>
                      <option value="tous">Tous</option>
                    </select>
                    <input
                      type="number"
                      value={t.pourcentage}
                      onChange={e => updateTranche(t.id, 'pourcentage', e.target.value)}
                      min="0.01"
                      max="100"
                      step="0.01"
                      className="input-pct"
                    />
                    <button
                      type="button"
                      className="tranche-del-btn"
                      onClick={() => supprimerTranche(t.id)}
                      disabled={form.tranches.length === 1}
                      title="Supprimer cette tranche"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                    {(errors[`tranche_heure_${i}`] || errors[`tranche_pct_${i}`]) && (
                      <p className="form-error tranche-err">
                        {errors[`tranche_heure_${i}`] || errors[`tranche_pct_${i}`]}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <button type="button" className="btn-add-tranche" onClick={ajouterTranche}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Ajouter une tranche
              </button>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={fermerFormulaire}>Annuler</button>
              <button type="button" className="btn-primary" onClick={valider}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {enEdition ? 'Enregistrer' : 'Créer le découpage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {decoupages.length === 0 && !formVisible && (
        <div className="dec-empty">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <p className="dec-empty-title">Aucun découpage défini</p>
          <p className="dec-empty-sub">
            Créez un découpage horaire pour ventiler le montant de chaque bénéficiaire
            sur des tranches de temps (ex : 60% de 8h à 12h, 40% de 12h à 18h).
          </p>
        </div>
      )}

      {decoupages.length > 0 && (
        <div className="dec-list">
          {decoupages.map(d => (
            <DecoupageCard
              key={d.id}
              decoupage={d}
              onEditer={() => editerDecoupage(d)}
              onSupprimer={() => onSupprimer(d.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CardProps {
  decoupage: Decoupage;
  onEditer: () => void;
  onSupprimer: () => void;
}

function DecoupageCard({ decoupage: d, onEditer, onSupprimer }: CardProps) {
  const totalPct = d.tranches.reduce((s, t) => s + t.pourcentage, 0);

  return (
    <div className="dec-card">
      <div className="dec-card-top">
        <div className="dec-card-info">
          <h3 className="dec-card-nom">{d.nom}</h3>
          <p className="dec-card-desc">
            {d.tranches.length} tranche{d.tranches.length > 1 ? 's' : ''} · total {totalPct}%
          </p>
        </div>
        <div className="dec-card-actions">
          <button className="dec-btn-icon" onClick={onEditer} title="Modifier">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button className="dec-btn-icon dec-btn-del" onClick={onSupprimer} title="Supprimer">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14H6L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/>
              <path d="M9 6V4h6v2"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="dec-tranches-list">
        {d.tranches.map((t, i) => (
          <div key={i} className="dec-tranche-pill">
            <span className="dec-tranche-heure">{fmtH(t.heureDebut)}–{fmtH(t.heureFin)}</span>
            <span className={`dec-tranche-type dec-type-${t.typeTransaction}`}>
              {TYPE_LABELS[t.typeTransaction]}
            </span>
            <span className="dec-tranche-pct">{t.pourcentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
